import PacksReportCustomerDrilldown from "./PacksReportCustomerDrilldown";
import PacksReportPackCustomersDrilldown from "./PacksReportPackCustomersDrilldown";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { connect, type ConnectedProps } from "react-redux";

import { bindActionCreators } from "@reduxjs/toolkit";

import { type RootState, type AppDispatch } from "features/Application/globaltypes/redux";
import { ContentView } from "components";
import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";
import { bindAction } from "interfaces";
import { setExportAction } from "features/Reporting/state/export/exportSlice";
import { setIsReportEnabled, reset } from "features/Reporting/state/toolbar/toolbarSlice";
import { useLicensesEngagementQuery } from "./Queries/queries";
import { type LicenseAccountRow } from "features/Reporting/types/packs";
import { usePerformanceBreadcrumbs } from "features/Library/Common/Hooks/usePerformanceBreadcrumbs";
import { dateRangeAllTime, isBsi, normalizePackStatusFilter } from "features/Library/Common/utils/performanceUtils";
import type { LicensesPackMetricsRow, PackIdAndName } from "./types";

import PacksReportMetricsByCustomerTable from "./PacksReportMetricsByCustomerTable";
import PacksReportMetricsByPackTable from "./PacksReportMetricsByPackTable";

import styles from "./packsReport.module.scss";
import CardReporting from "components/cards/CardReporting/CardReporting";
import ReportingFilter from "components/reportingFilter/ReportingFilter";
import { usePerformanceFilter } from "features/Library/Common/Hooks/usePerformanceFilter";
import { useAccountFilterShouldShow } from "features/Reporting/Content/queries/useAccountFilterShouldShow";
import ReportingExport from "components/reportingExport/ReportingExport";
import cn from "classnames";
import { doLicensesAccountsExport, doLicensesPacksOverviewExport } from "./Queries/doLicensesAccountPacksExport";
import { ReportUnavailable } from "components/reportUnavailable/ReportUnavailable";
import { FeatureFlags } from "featureFlags";
import { useFeatureFlag } from "hooks/useFeatureFlag";

const PacksReport: FC<PropsFromRedux> = ({
  accountId,
  setExportAction,
  setIsReportEnabled,
  resetIsReportEnabled,
  accountType,
  licensesAccountsExport,
  licensesPacksOverviewExport,
}) => {
  const reportEnabled = useFeatureFlag(FeatureFlags.PacksReport);
  const accountFilterShouldShow = useAccountFilterShouldShow(accountId, accountType, true);
  const initialDateRange = useMemo(() => dateRangeAllTime(), []);
  const initialOverrides = useMemo(() => (!isBsi(accountId) ? { type: "account" as const } : undefined), [accountId]);
  const [filter, setFilter] = usePerformanceFilter({
    accountId,
    includeAccountsDropdown: accountFilterShouldShow,
    supportsSPType: true,
    includePacks: true,
    initialDateRange,
    initialOverrides,
  });

  const [selectedAccount, setSelectedAccount] = useState<{ id: number; name: string } | null>(null);
  const [selectedPack, setSelectedPack] = useState<PackIdAndName | null>(null);

  const { domElements, registerBreadcrumb, hasBreadcrumbsToDisplay } = usePerformanceBreadcrumbs();

  const normalizedFilter = useMemo(
    () => ({
      ...filter,
      packStatusId: normalizePackStatusFilter(filter.packStatusId!),
    }),
    [filter],
  );

  const selectedPackFilter = useMemo(
    () =>
      selectedPack
        ? {
            ...normalizedFilter,
            packId: [{ value: selectedPack.packId, text: selectedPack.packName }],
          }
        : null,
    [normalizedFilter, selectedPack],
  );

  const engagementQuery = useLicensesEngagementQuery(normalizedFilter);

  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    // Customer and Pack drill downs manage their own export action.
    if (selectedAccount !== null || selectedPack !== null) {
      return;
    }

    setExportAction({
      method: activeTab === 0 ? licensesAccountsExport : licensesPacksOverviewExport,
      args: [normalizedFilter, activeTab === 0 ? "Metrics by Customer Export" : "Metrics by Pack Export"],
      isExportEnabled: true,
    });
  }, [
    activeTab,
    licensesAccountsExport,
    licensesPacksOverviewExport,
    normalizedFilter,
    selectedAccount,
    selectedPack,
    setExportAction,
  ]);

  useEffect(() => {
    setIsReportEnabled(reportEnabled);

    return () => {
      resetIsReportEnabled();
    };
  }, [reportEnabled, resetIsReportEnabled, setIsReportEnabled]);

  const handleAccountSelection = useCallback(
    (row: LicenseAccountRow) => {
      setSelectedAccount({ id: row.CustomerId, name: row.CustomerName });
      registerBreadcrumb({
        text: row.CustomerName,
        action: () => {
          setSelectedAccount(null);
          setSelectedPack(null);
        },
      });
    },
    [registerBreadcrumb],
  );

  useEffect(() => {
    if (selectedPack) {
      registerBreadcrumb({ text: selectedPack.packName, action: () => setSelectedPack(null) });
    }
  }, [selectedPack, registerBreadcrumb]);

  const handlePackSelection = useCallback((row: LicensesPackMetricsRow) => {
    setSelectedPack({ packId: row.PackId, packName: row.PackName });
  }, []);

  const ignoreInCountAndHide = useMemo(() => {
    if (selectedAccount) {
      return ["type", "accounts", "includeMyData", "dates"];
    }

    if (activeTab === 1 && selectedPack) {
      return ["type", "dates", "packId"];
    }

    return ["type", "dates"];
  }, [activeTab, selectedAccount, selectedPack]);

  const customerFilter = selectedAccount
    ? {
        ...normalizedFilter,
        accounts: [selectedAccount.id],
        includeMyData: false,
      }
    : null;

  if (!reportEnabled) {
    return (
      <div className={styles.reportUnavailable}>
        <ReportUnavailable />
      </div>
    );
  }

  return (
    <div className={"performanceRoot"}>
      <div className="performanceHeader">
        <div className={cn("performanceHeader", styles.header)}>
          {hasBreadcrumbsToDisplay ? (
            <div className="performanceBreadcrumbs">{domElements}</div>
          ) : (
            <h2 className="performanceTitle">Summary</h2>
          )}
          {!(selectedPack && selectedAccount) && (
            <div className="performanceActions">
              {/* @ts-ignore */}
              <ReportingFilter
                currentFilter={filter}
                callback={setFilter}
                includeAccountsDropdown={accountFilterShouldShow}
                ignoreInCountAndHide={ignoreInCountAndHide}
                supportsSPType
                includePacks
                initialDateRange={initialDateRange}
                overrideAccountId={selectedAccount?.id}
                initialOverrides={initialOverrides}
              />
              {<ReportingExport />}
            </div>
          )}
        </div>
      </div>
      <div className={styles.content}>
        {selectedAccount && (
          <PacksReportCustomerDrilldown
            filter={customerFilter!}
            setSelectedPack={setSelectedPack}
            selectedPack={selectedPack}
            selectedAccount={selectedAccount}
          />
        )}
        {selectedPack && !selectedAccount && <PacksReportPackCustomersDrilldown filter={selectedPackFilter!} />}
        {!selectedAccount && !selectedPack && (
          <>
            <div className={styles.cards}>
              <RequestStatusRenderer state={engagementQuery.status}>
                {engagementQuery.isSuccess && engagementQuery.data && (
                  <CardReporting items={engagementQuery.data.packsCards} />
                )}
              </RequestStatusRenderer>
            </div>

            <div className={styles.tableSection}>
              <div className={styles.tabs}>
                <ContentView
                  tabs={[
                    {
                      id: "metricsByCustomer",
                      label: "Metrics by Customer",
                      component: (
                        <PacksReportMetricsByCustomerTable
                          filter={normalizedFilter}
                          onCustomerSelected={handleAccountSelection}
                        />
                      ),
                    },
                    {
                      id: "metricsByPack",
                      label: "Metrics by Pack",
                      component: (
                        <PacksReportMetricsByPackTable filter={normalizedFilter} onPackSelected={handlePackSelection} />
                      ),
                    },
                  ]}
                  activeIndex={activeTab}
                  onTabChange={(_, r: { activeIndex: number }) => {
                    setActiveTab(r.activeIndex);
                  }}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  accountId: state.userProfile.accountId,
  accountType: state.userProfile.accountTypeId,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  setExportAction: bindAction(setExportAction, dispatch),
  setIsReportEnabled: bindAction(setIsReportEnabled, dispatch),
  resetIsReportEnabled: bindAction(reset, dispatch),
  licensesAccountsExport: bindActionCreators(doLicensesAccountsExport, dispatch),
  licensesPacksOverviewExport: bindActionCreators(doLicensesPacksOverviewExport, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(PacksReport);
export default ConnectedComponent;
